/**
 * Shown when a raster / image is opened without an earth CRS.
 * The file still loads at its current (often arbitrary) location; this
 * dialog only asks how the operator wants to position it.
 */

import { Callout, Dialog, Icon, tokens } from '@/design';

export interface MissingCrsDialogProps {
  readonly open: boolean;
  readonly layerLabel: string;
  readonly onRectify: () => void;
  readonly onFakeCoordinates: () => void;
  readonly onDismiss: () => void;
  readonly 'data-testid'?: string;
}

const optionStyle = (selected: boolean) => ({
  display: 'flex' as const,
  flexDirection: 'column' as const,
  gap: tokens.space[1],
  width: '100%',
  textAlign: 'left' as const,
  padding: tokens.space[3],
  minHeight: tokens.density.touchMin,
  background: selected ? tokens.color.accent.subtle : tokens.color.surface[2],
  color: tokens.color.ink.primary,
  border: selected
    ? `${tokens.border.width.thin + 1}px solid ${tokens.color.accent.primary}`
    : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
  borderRadius: tokens.radius.lg,
  cursor: 'pointer' as const,
  fontFamily: tokens.font.family.ui,
});

export function MissingCrsDialog({
  open,
  layerLabel,
  onRectify,
  onFakeCoordinates,
  onDismiss,
  'data-testid': testId = 'missing-crs-dialog',
}: MissingCrsDialogProps) {
  if (!open) return null;

  return (
    <Dialog
      title="Unknown spatial reference"
      size="md"
      role="alertdialog"
      blockBackdropClose
      onClose={onDismiss}
      data-testid={testId}
      actions={[
        { label: 'Decide later', variant: 'ghost', onClick: onDismiss, defaultFocus: true },
      ]}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[3],
        }}
      >
        <Callout severity="warning" icon="gps_off">
          GeoViewer was unable to determine the earth reference coordinate for
          the image{layerLabel ? ` “${layerLabel}”` : ''}. Select how you would
          like to position the image.
        </Callout>
        <p
          style={{
            margin: 0,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.body.size,
            color: tokens.color.ink.secondary,
          }}
        >
          The file is already on the map at its current location. That is
          expected when no coordinate system is present.
        </p>
        <button
          type="button"
          onClick={onRectify}
          data-testid="missing-crs-rectify"
          style={optionStyle(false)}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              fontWeight: tokens.font.weight.semibold,
            }}
          >
            <Icon name="pin_drop" size={20} />
            Manually rectify the image
          </span>
          <span
            style={{
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
            }}
          >
            Pick matching points on the image and on the earth (minimum 3) to
            overlap the two, the same way ArcGIS Pro georeferencing works.
          </span>
        </button>
        <button
          type="button"
          onClick={onFakeCoordinates}
          data-testid="missing-crs-fake-coords"
          style={optionStyle(false)}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              fontWeight: tokens.font.weight.semibold,
            }}
          >
            <Icon name="visibility" size={20} />
            Fake coordinates to view
          </span>
          <span
            style={{
              fontSize: tokens.font.role.engMeta.size,
              color: tokens.color.ink.muted,
            }}
          >
            Keep the current placement and just view the image. You can still
            open Georeference from the layer tools later.
          </span>
        </button>
      </div>
    </Dialog>
  );
}
